import React from 'react';
import styled, { keyframes } from 'styled-components';
import { TokenSet } from '../../styles/tokens';
import { Button } from './Button';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(3px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalBox = styled.div<{ tokens: TokenSet }>`
  background: ${p => p.tokens.bgSurface};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 380px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  animation: ${slideUp} 0.25s cubic-bezier(0.16, 1, 0.3, 1);
`;

const Title = styled.h3<{ tokens: TokenSet }>`
  font-size: 18px;
  font-weight: 800;
  color: ${p => p.tokens.text};
  margin: 0 0 8px 0;
`;

const Body = styled.p<{ tokens: TokenSet }>`
  font-size: 14px;
  color: ${p => p.tokens.textMuted};
  margin: 0 0 18px 0;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

interface ConfirmModalProps {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  tokens: TokenSet;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title, body, confirmLabel, cancelLabel, onConfirm, onCancel, tokens,
}) => (
  <Backdrop onClick={onCancel}>
    <ModalBox tokens={tokens} onClick={e => e.stopPropagation()}>
      <Title tokens={tokens}>{title}</Title>
      <Body tokens={tokens}>{body}</Body>
      <Actions>
        <Button tokens={tokens} variant="secondary" size="sm" onClick={onCancel} type="button">
          {cancelLabel}
        </Button>
        <Button tokens={tokens} size="sm" onClick={onConfirm} type="button">
          {confirmLabel}
        </Button>
      </Actions>
    </ModalBox>
  </Backdrop>
);
